import React from 'react';
import LandingHeader from '../../ui/LandingHeader';
import LandingFooter from '../../ui/LandingFooter';
import CallToAction from '../CallToAction';
import { cn } from '@/lib/utils';
import { Users, MessageSquare, GitBranch, Share2, Clock, Star } from 'lucide-react';

const features = [
    {
        title: "Real-time Collaboration",
        description: "Edit code together in real-time with multiple team members, just like Google Docs for code. See changes as they happen.",
        icon: Users,
    },
    {
        title: "Team Chat & Comments",
        description: "Discuss code and leave contextual comments directly within the editor. Keep all communication in one place.",
        icon: MessageSquare,
    },
    {
        title: "Version Control",
        description: "Built-in version control with Git integration and conflict resolution. Keep track of changes and collaborate safely.",
        icon: GitBranch,
    },
    {
        title: "Project Sharing",
        description: "Share your projects with team members and control access levels. Collaborate with the right people at the right time.",
        icon: Share2,
    }
];

const TeamCollaborationPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-background">
            <div className="relative z-10">
                <LandingHeader />
            </div>
            
            <main className="container mx-auto px-4 py-6 sm:py-12 flex-grow mt-16">
                <div className="mx-auto max-w-[980px]">
                    {/* Hero Section */}
                    <div className="flex flex-col items-center text-center space-y-4 mb-12">
                        <div className="rounded-full bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary">
                            Team Collaboration
                        </div>
                        <h1 className={cn(
                            "text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl",
                            "bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent"
                        )}>
                            Build Together, Better
                        </h1>
                        <p className="mx-auto max-w-[700px] text-lg text-muted-foreground sm:text-xl">
                            Work together seamlessly with your team in real-time. Share, discuss, and build amazing applications together.
                        </p>
                    </div>

                    {/* Demo Section */}
                    <div className="mb-16">
                        <div className="overflow-hidden rounded-lg border bg-background shadow-xl">
                            <div className="flex h-14 items-center justify-between border-b px-4">
                                <div className="flex items-center space-x-3">
                                    <Users className="h-5 w-5 text-primary" />
                                    <span className="text-sm font-medium">Team Workspace</span>
                                </div>
                            </div>
                            <div className="h-[600px] overflow-hidden">
                                <div className="p-6 overflow-y-auto h-full">
                                    <div className="space-y-6">
                                        {/* Team Members */}
                                        <div className="flex items-center gap-4">
                                            <div className="flex -space-x-2">
                                                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium">SC</div>
                                                <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-medium">MJ</div>
                                                <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white font-medium">ED</div>
                                                <div className="w-10 h-10 rounded-full bg-yellow-500 flex items-center justify-center text-white font-medium">RK</div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm text-gray-500">4 team members online</span>
                                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                            </div>
                                        </div>

                                        {/* Recent Activity */}
                                        <div className="space-y-4">
                                            <h3 className="text-sm font-medium text-gray-900">Recent Activity</h3>
                                            <div className="space-y-3">
                                                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                                    <div className="p-2 bg-white rounded-md border">
                                                        <MessageSquare className="w-4 h-4 text-gray-600" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium">Sarah Chen</p>
                                                        <p className="text-xs text-gray-600">Updated sales dashboard</p>
                                                        <p className="text-xs text-gray-400 mt-1">2 min ago</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                                    <div className="p-2 bg-white rounded-md border">
                                                        <GitBranch className="w-4 h-4 text-gray-600" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium">Mike Johnson</p>
                                                        <p className="text-xs text-gray-600">Created new branch</p>
                                                        <p className="text-xs text-gray-400 mt-1">15 min ago</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                                    <div className="p-2 bg-white rounded-md border">
                                                        <MessageSquare className="w-4 h-4 text-gray-600" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium">Emma Davis</p>
                                                        <p className="text-xs text-gray-600">Added comments</p>
                                                        <p className="text-xs text-gray-400 mt-1">1 hour ago</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Project Stats */}
                                        <div className="grid grid-cols-3 gap-4">
                                            <div className="bg-gray-50 rounded-lg p-4">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Clock className="w-4 h-4 text-gray-500" />
                                                    <p className="text-sm text-gray-500">Last Updated</p>
                                                </div>
                                                <p className="text-sm font-medium text-gray-900">2 minutes ago</p>
                                            </div>
                                            <div className="bg-gray-50 rounded-lg p-4">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Users className="w-4 h-4 text-gray-500" />
                                                    <p className="text-sm text-gray-500">Collaborators</p>
                                                </div>
                                                <p className="text-sm font-medium text-gray-900">4 team members</p>
                                            </div>
                                            <div className="bg-gray-50 rounded-lg p-4">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Star className="w-4 h-4 text-gray-500" />
                                                    <p className="text-sm text-gray-500">Project Status</p>
                                                </div>
                                                <p className="text-sm font-medium text-gray-900">In Progress</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Features Grid */}
                    <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 mb-16">
                        {features.map((feature, i) => (
                            <div
                                key={i}
                                className="relative overflow-hidden rounded-lg border bg-background p-6"
                            >
                                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                                    <feature.icon className="h-6 w-6 text-primary" />
                                </div>
                                <div className="mt-4 space-y-2">
                                    <h3 className="font-bold">{feature.title}</h3>
                                    <p className="text-muted-foreground text-sm">
                                        {feature.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <CallToAction />
            </main>
            <LandingFooter />
        </div>
    );
};

export default TeamCollaborationPage; 